import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { CircleAlert } from "lucide-react";
import { BoardAction } from "../dtos";
import { actionCtaLabel } from "../utils/derive-board";
import { primaryButtonSx } from "./field-shell";

/**
 * "Upcoming actions" modal — organized by VERDICT, tone-matched to the rest of the tab
 * (product ask, 2026-08-05): rejections/edits first on the red ramp, conditional approvals on
 * the orange ramp, then the plain next action and the curated upcoming list. Selecting an
 * action jumps to — and expands — its step card.
 */
const UpcomingActionsModal = ({
  open,
  onClose,
  nextAction,
  nextAgentAction = null,
  urgentActions,
  upcomingActions,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  /** §38 (QA): the immediate actionable step — leads its group as a highlighted block */
  nextAction?: BoardAction | null;
  /** §NextForAgent: the step that WILL land on the agent — the modal's subject when nothing
   *  is open for them (mirrors the bar's "Up next for you" instead of an empty state) */
  nextAgentAction?: BoardAction | null;
  urgentActions: BoardAction[];
  upcomingActions: BoardAction[];
  onSelect: (action: BoardAction) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;

  const isRed = (action: BoardAction) =>
    action.kind === "BLOCKING" || action.kind === "EDITS";
  // §38: the immediate action leads ITS group; §39: upcoming stays short and curated.
  const needsFixing = [
    ...(nextAction && isRed(nextAction) && !urgentActions.some((a) => a.stepUid === nextAction.stepUid)
      ? [nextAction]
      : []),
    ...urgentActions.filter(isRed),
  ];
  const conditionals = urgentActions.filter((action) => action.kind === "CONDITIONAL");
  const plainNext =
    nextAction && !isRed(nextAction) && nextAction.kind !== "CONDITIONAL" ? nextAction : null;
  const curatedUpcoming = upcomingActions
    .filter((action) => action.stepUid !== plainNext?.stepUid)
    .slice(0, 3);

  /** design ([Mobile] Upcoming actions, nodes 12107:59094 / 59098): every group label and the
   *  block's own caption are Body/xs - Regular 11/16 with zero tracking. MUI's `caption` is
   *  12 / 19.92 / 0.4px, which is a step too large with a taller line box. */
  const labelSx = {
    fontSize: 11,
    lineHeight: "16px",
    fontWeight: 400,
    letterSpacing: 0,
    color: colors.semantic.text.secondary,
  } as const;

  const rowSubtitle = (action: BoardAction) =>
    action.subtitle ? (
      <Typography
        variant="caption"
        color={colors.semantic.text.secondary}
        sx={{
          // the step intro can be a paragraph — two lines is context, more is noise here
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {action.subtitle}
      </Typography>
    ) : null;

  const actionRow = (action: BoardAction, titleColor?: string, iconColor?: string) => (
    <Stack
      key={action.stepUid}
      direction="row"
      // design (12107:59099): item cards are radius 12 with a 16px gap between title and CTA
      spacing={2}
      alignItems="center"
      sx={{
        backgroundColor: colors.semantic.layer.primary,
        borderRadius: theme.nawy.radius.xl,
        p: 1.5,
      }}
    >
      {iconColor && <CircleAlert size={16} color={iconColor} style={{ flexShrink: 0 }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* design (12107:59100): row title is Body/md - Regular 14/20 and WRAPS (flex-[1_0_0]
            min-w-px, no nowrap). This was 16/24 semibold + ellipsised. */}
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            color: titleColor,
            wordBreak: "break-word",
          }}
        >
          {action.title}
        </Typography>
        {rowSubtitle(action)}
      </Box>
      {action.hasCta && (
        // §MobileQA: `variant="contained"` with no palette override rendered MUI's DEFAULT
        // primary (#1976d2) at 13px — the one blue in the tab that was never the brand's.
        <Button
          variant="contained"
          disableElevation
          onClick={() => onSelect(action)}
          sx={{ ...primaryButtonSx(theme), textTransform: "none", flexShrink: 0 }}
        >
          {actionCtaLabel(action)}
        </Button>
      )}
    </Stack>
  );

  const section = (
    label: string,
    rows: React.ReactNode,
    ground?: string,
    edge?: string
  ) => (
    <Box
      sx={{
        backgroundColor: ground,
        ...(edge ? { border: `1px solid ${edge}` } : {}),
        borderRadius: theme.nawy.radius.lg,
        // design (12107:59097): blocks are px 16 / py 12, with 12px between the label and the
        // first card and between the cards themselves (this was 16 all round / 4 / 8).
        px: 2,
        py: 1.5,
        mb: 2,
        "&:last-child": { mb: 0 },
      }}
    >
      <Typography sx={labelSx}>{label}</Typography>
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        {rows}
      </Stack>
    </Box>
  );

  /** design (12107:59092): the immediate-action block is a SINGLE row sitting directly on the
   *  accent-light ground inside a 0.5px accent hairline — the label and the step title stack in
   *  the left column with the CTA centred against both. There is no nested white card in it,
   *  which is why it doesn't go through section() + actionRow(). */
  const urgentBlock = (label: string, action: BoardAction) => (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        backgroundColor: colors.semantic.layer.accentLight,
        border: `0.5px solid ${colors.semantic.border.accent}`,
        borderRadius: theme.nawy.radius.lg,
        px: 2,
        py: 1.5,
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            color: colors.semantic.text.accent,
            wordBreak: "break-word",
          }}
        >
          {action.title}
        </Typography>
        {rowSubtitle(action)}
      </Box>
      {action.hasCta && (
        <Button
          variant="contained"
          disableElevation
          onClick={() => onSelect(action)}
          sx={{ ...primaryButtonSx(theme), textTransform: "none", flexShrink: 0 }}
        >
          {actionCtaLabel(action)}
        </Button>
      )}
    </Stack>
  );

  return (
    <ResponsiveModal open={open} onClose={onClose} title="Upcoming actions">
      <Box
        sx={{
          // design (12107:59091): the content block is inset 16 on every side. MobileModal
          // already pads its children by spacing.md, so padding here would double it; the
          // desktop CommonModal pads nothing, so it has to be supplied on that branch.
          p: { mobile: 0, tablet: 2 },
          // nawyTheme breakpoints are mobile/tablet/desktop — xs/md silently no-op
          width: { mobile: "100%", desktop: 520 },
          // §MobileQA (2026-08-16): on a phone the SHEET owns the scroll (MobileModal's
          // Sheet.Scroller) — a 70vh inner cap measured against the LARGE viewport clipped the
          // list mid-row and left the sheet's tail empty ("el menu ma2tooma w b2y l page fadya").
          maxHeight: { mobile: "none", desktop: "70vh" },
          overflowY: { mobile: "visible", desktop: "auto" },
        }}
      >
        {/* rejections / sent-backs — act first */}
        {needsFixing.length > 0 &&
          section(
            "Needs fixing",
            needsFixing.map((action) =>
              actionRow(
                action,
                colors.semantic.text.extension.negative,
                colors.primitives.red[500]
              )
            ),
            colors.semantic.layer.extension.negativeLight,
            colors.primitives.red[200]
          )}

        {/* approved WITH conditions — read the note, no re-upload owed */}
        {conditionals.length > 0 &&
          section(
            "Conditional approvals",
            conditionals.map((action) => actionRow(action, "#E65F00", "#F76700")),
            "#FFF6F0",
            "#F76700"
          )}

        {/* the plain next step in the flow */}
        {plainNext && urgentBlock("Next action", plainNext)}

        {/* §NextForAgent: nothing open for the agent — the modal names what WILL be theirs,
            exactly like the bar that opened it */}
        {!plainNext &&
          !needsFixing.length &&
          !conditionals.length &&
          nextAgentAction &&
          urgentBlock("Up next for you", nextAgentAction)}

        {section(
          "Upcoming actions",
          curatedUpcoming.length > 0 ? (
            curatedUpcoming.map((action) =>
              actionRow(action, colors.semantic.text.accent)
            )
          ) : (
            <Typography variant="body2" color={colors.semantic.text.secondary}>
              No upcoming actions
            </Typography>
          ),
          // design (12107:59097) grounds this block on Layer/Secondary #F4F7F8. The semantic
          // token resolves to navy[50] #EEF3F5 — a cooler grey — so the design's value is taken
          // from the primitive it actually maps to rather than retuning the shared token.
          colors.primitives.white[200]
        )}
      </Box>
    </ResponsiveModal>
  );
};

export default UpcomingActionsModal;
